import React, { useMemo } from "react";
import { Layers, Plus, Trash2, Globe, Server, CreditCard, Network, RotateCcw, Sparkles, Check } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";
import { getMethodologiesForTargets } from "../../../../../utils/sowMethodologies";

interface MethodologyCoverageEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
}

const defaultMethodology =
  "CTB will perform the assessment in alignment with recognised penetration testing guidance, including the Penetration Testing Execution Standard (PTES), NIST SP 800-115, the OWASP Web Security Testing Guide, the OWASP Top 10, the OWASP API Security Top 10, and the OWASP Mobile Application Security Verification Standard (MASVS), where relevant to the authorised attack surface. Testing combines automated discovery and vulnerability analysis with focused manual testing and validation by experienced consultants: automated techniques map the authorised attack surface and identify candidate issues, and manual testing validates exploitability, removes false positives, investigates business-logic weaknesses, and assesses real-world impact. All testing is performed within the authorised boundaries and with reasonable care to minimise disruption to the production environment.";

const defaultCoverageWeb = `Authentication and session management, including registration, login, credential recovery, token and cookie handling, and session lifecycle
Authorisation and access-control enforcement between the standard and privileged access tiers, including horizontal escalation between peer accounts and vertical escalation toward privileged and administrative functionality
Security of administrative tooling exposed to the privileged access tier, including user and account-related functionality accessible through the authorised in-scope roles, where applicable
The platform's core update and delivery workflows and associated in-scope functionality, including the demonstration workflow available to the standard access tier
Common web application weaknesses aligned with the OWASP Top 10, including injection, cross-site scripting, insecure direct object references, and insecure configuration
Handling and exposure of sensitive data within the authorised application surfaces`;

const defaultCoverageApi = `Broken object-level authorisation (BOLA) and broken function-level authorisation across the in-scope API and integration surface
Authentication and authorisation enforcement, including token handling for the in-scope access tiers
Validation and handling of inbound requests to the authorised partner integration receiver endpoint, including input validation, message integrity, and replay considerations
Mass assignment, excessive data exposure, and error-handling weaknesses
Business logic and rate-limiting weaknesses relevant to the in-scope functional areas, aligned with the OWASP API Security Top 10`;

const defaultCoveragePayment = `Integrity and authorisation of the payment workflow exposed to the privileged access tier, exercised in the Client's Stripe test environment (test mode) using Stripe test cards.
Enforcement of the boundary between the demonstration workflow available to the standard access tier and the live workflow available to the privileged access tier
Security of the identity-verification path as exercised against the Client's non-billable sandbox`;

const defaultCoverageNetwork = `Service discovery and enumeration against the authorised external network host
Review of exposed services, versions, and externally observable configuration weaknesses
Validation of externally reachable weaknesses identified during discovery`;

export const MethodologyCoverageEditor: React.FC<MethodologyCoverageEditorProps> = ({
  fields,
  setField
}) => {
  const lifecycle = fields.executionLifecycle || [];
  const targetItems = fields.targetItems || [];

  // Compute detected standardised methodologies based on current targets
  const detectedMethodologies = useMemo(() => {
    return getMethodologiesForTargets(targetItems);
  }, [targetItems]);

  const handleUpdatePhase = (index: number, updates: { phase?: string; description?: string }) => {
    const updated = [...lifecycle];
    updated[index] = { ...updated[index], ...updates };
    setField("executionLifecycle", updated);
  };

  const handleAddPhase = () => {
    setField("executionLifecycle", [
      ...lifecycle,
      { phase: "New Execution Phase", description: "Phase description and activities." }
    ]);
  };

  const handleRemovePhase = (index: number) => {
    if (lifecycle.length <= 1) {
      alert("At least one lifecycle phase is required.");
      return;
    }
    const filtered = lifecycle.filter((_, idx) => idx !== index);
    setField("executionLifecycle", filtered);
  };

  // Sync / populate coverage text areas from detected methodologies
  const handleAutoPopulateFromTargets = () => {
    const webMod = detectedMethodologies.find((m) => m.category === "WEB");
    const apiMod = detectedMethodologies.find((m) => m.category === "API");
    const netMod = detectedMethodologies.find((m) => m.category === "NETWORK");

    if (webMod) {
      setField("coverageWebText", webMod.bullets.join("\n"));
    }
    if (apiMod) {
      setField("coverageApiText", apiMod.bullets.join("\n"));
    }
    if (netMod) {
      setField("coverageNetworkText", netMod.bullets.join("\n"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Layers size={16} className="text-ctb-blue" />
          Section 4: Testing Methodology & Coverage Areas
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Configure the testing standard framework (PTES, OWASP, NIST), the execution phases lifecycle table, and the dynamic methodology coverage.
        </p>
      </div>

      {/* Dynamic Target Detection Banner */}
      <div className="p-3.5 bg-blue-50/70 border border-blue-200 rounded-xl space-y-2 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-bold text-blue-950 flex items-center gap-1.5 min-w-0">
            <Sparkles size={14} className="text-ctb-blue shrink-0" />
            <span>Active Target Methodologies (Auto-Selected from Scope)</span>
          </span>
          <button
            type="button"
            onClick={handleAutoPopulateFromTargets}
            className="text-xs font-bold text-white bg-ctb-blue hover:bg-ctb-blue/90 px-3 py-1.5 rounded-lg transition shadow-xs flex items-center gap-1.5 cursor-pointer shrink-0 ml-auto"
            title="Populate editors with exact bullet points from detected methodologies"
          >
            <Check size={13} className="shrink-0" />
            <span className="whitespace-nowrap">Sync Textareas</span>
          </button>
        </div>
        <p className="text-[11px] text-blue-800 leading-relaxed">
          The PDF dynamically renders the exact standardised methodology (Blackbox / Graybox) matching your authorised targets in Section 3.
        </p>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {detectedMethodologies.map((m) => (
            <span
              key={m.id}
              className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-white border border-blue-200 text-blue-900 shadow-xs"
            >
              {m.title}
            </span>
          ))}
        </div>
      </div>

      {/* Methodology Overview */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800">Testing Methodology Narrative</label>
          <button
            type="button"
            onClick={() => setField("testingMethodologyText", defaultMethodology)}
            className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw size={11} />
            Reset to default
          </button>
        </div>
        <textarea
          rows={5}
          value={fields.testingMethodologyText ?? ""}
          onChange={(e) => setField("testingMethodologyText", e.target.value)}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
      </div>

      {/* Execution Lifecycle Table */}
      <div className="space-y-3 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-xs font-bold text-slate-800">Execution Lifecycle (Phases Table)</label>
            <p className="text-[11px] text-slate-500">The structured sequential workflow followed during fieldwork.</p>
          </div>
          <button
            type="button"
            onClick={handleAddPhase}
            className="px-2.5 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-lg hover:bg-slate-900 flex items-center gap-1 cursor-pointer transition shadow-sm"
          >
            <Plus size={13} />
            Add Phase
          </button>
        </div>

        <div className="space-y-2">
          {lifecycle.map((row, idx) => (
            <div
              key={idx}
              className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3 hover:border-slate-300 transition"
            >
              <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-1">
                {idx + 1}
              </span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <input
                    type="text"
                    value={row.phase}
                    onChange={(e) => handleUpdatePhase(idx, { phase: e.target.value })}
                    placeholder="Phase Name"
                    className="w-full text-xs font-semibold bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                  />
                </div>
                <div className="md:col-span-8">
                  <input
                    type="text"
                    value={row.description}
                    onChange={(e) => handleUpdatePhase(idx, { description: e.target.value })}
                    placeholder="Phase Description"
                    className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemovePhase(idx)}
                disabled={lifecycle.length <= 1}
                className="text-slate-400 hover:text-rose-600 p-1 rounded-md transition disabled:opacity-30 cursor-pointer shrink-0 mt-1"
                title="Remove Phase"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Coverage Areas (Custom Overrides) */}
      <div className="space-y-4 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800">Coverage Areas (Optional Custom Text Overrides)</label>
          <span className="text-[10px] text-slate-500">
            If left default or empty, the PDF automatically renders the full standardised methodology bullets.
          </span>
        </div>

        {/* Web */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Globe size={13} className="text-ctb-blue" />
              Web Application Security Coverage
            </label>
            <button
              type="button"
              onClick={() => setField("coverageWebText", defaultCoverageWeb)}
              className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw size={11} />
              Reset to default
            </button>
          </div>
          <textarea
            rows={4}
            value={fields.coverageWebText ?? ""}
            onChange={(e) => setField("coverageWebText", e.target.value)}
            placeholder="Web application test scope..."
            className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
          />
        </div>

        {/* API */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Server size={13} className="text-purple-600" />
              API and Integration Security Coverage
            </label>
            <button
              type="button"
              onClick={() => setField("coverageApiText", defaultCoverageApi)}
              className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw size={11} />
              Reset to default
            </button>
          </div>
          <textarea
            rows={4}
            value={fields.coverageApiText ?? ""}
            onChange={(e) => setField("coverageApiText", e.target.value)}
            placeholder="API testing scope..."
            className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
          />
        </div>

        {/* Payment */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <CreditCard size={13} className="text-emerald-600" />
              Payment and Identity Verification Paths Coverage
            </label>
            <button
              type="button"
              onClick={() => setField("coveragePaymentText", defaultCoveragePayment)}
              className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw size={11} />
              Reset to default
            </button>
          </div>
          <textarea
            rows={4}
            value={fields.coveragePaymentText ?? ""}
            onChange={(e) => setField("coveragePaymentText", e.target.value)}
            placeholder="Payment workflow test scope..."
            className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
          />
        </div>

        {/* Network */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Network size={13} className="text-cyan-600" />
              External & Internal Network Coverage
            </label>
            <button
              type="button"
              onClick={() => setField("coverageNetworkText", defaultCoverageNetwork)}
              className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw size={11} />
              Reset to default
            </button>
          </div>
          <textarea
            rows={3}
            value={fields.coverageNetworkText ?? ""}
            onChange={(e) => setField("coverageNetworkText", e.target.value)}
            placeholder="Network infrastructure scope..."
            className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
          />
        </div>
      </div>
    </div>
  );
};
